import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  GraduationCap, Brain, Play, CheckCircle2, XCircle, Clock,
  TrendingUp, Sparkles, AlertCircle, RefreshCw, Activity, FileText
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryColors = {
  questioning: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  severity: 'bg-red-500/20 text-red-300 border-red-500/30',
  missed_detail: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  accuracy: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  style: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  general: 'bg-slate-600/40 text-slate-300 border-slate-500/30',
};

const TrainingCenter = () => {
  const { user, isSuperAdmin } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [numSims, setNumSims] = useState('2');

  const [overview, setOverview] = useState(null);
  const [rules, setRules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');

  const [loading, setLoading] = useState(false);
  const [training, setTraining] = useState(false);
  const [proposing, setProposing] = useState(false);
  const [learningSessions, setLearningSessions] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // ── Load tenants ──────────────────────────────────────────────
  useEffect(() => {
    const loadTenants = async () => {
      try {
        let list = [];
        if (isSuperAdmin && isSuperAdmin()) {
          const res = await axios.get(`${API}/tenants`);
          list = res.data || [];
        } else {
          list = (user?.tenants || []).map((t) => ({
            tenant_id: t.tenant_id,
            name: t.name,
            industry_type: t.industry_type,
          }));
        }
        setTenants(list);
        if (list.length > 0) setSelectedTenant(list[0].tenant_id);
      } catch (e) {
        console.error(e);
        setError('Failed to load tenants');
      }
    };
    loadTenants();
  }, [user, isSuperAdmin]);

  // ── Load data for selected tenant ─────────────────────────────
  const loadData = useCallback(async (tenantId) => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const [ov, rl, rn] = await Promise.all([
        axios.get(`${API}/training/overview/${tenantId}`),
        axios.get(`${API}/training/rules/${tenantId}`),
        axios.get(`${API}/training/runs/${tenantId}`),
      ]);
      setOverview(ov.data);
      setRules(rl.data || []);
      setRuns(rn.data || []);
    } catch (e) {
      console.error(e);
      setError('Failed to load training data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTenant) loadData(selectedTenant);
  }, [selectedTenant, loadData]);

  // ── Actions ───────────────────────────────────────────────────
  const runTraining = async () => {
    if (!selectedTenant) return;
    setTraining(true);
    setError(null);
    setNotice(null);
    try {
      // 1) Start the run in the background (returns immediately)
      const startRes = await axios.post(`${API}/training/simulate`, {
        tenant_id: selectedTenant,
        num_simulations: parseInt(numSims, 10),
      });
      const runId = startRes.data?.run_id;
      if (!runId) throw new Error('No run id returned');

      // 2) Poll for completion (up to ~4 minutes)
      const maxAttempts = 80; // 80 * 3s = 240s
      let finalRun = null;
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const runRes = await axios.get(`${API}/training/run/${runId}`);
          const status = runRes.data?.status;
          if (status === 'completed' || status === 'failed') {
            finalRun = runRes.data;
            break;
          }
        } catch (pollErr) {
          // transient poll error - keep trying
          console.warn('poll error', pollErr);
        }
      }

      if (finalRun && finalRun.status === 'completed') {
        const proposed = finalRun.rules_proposed ?? 0;
        const acc = finalRun.avg_accuracy;
        setNotice(
          `Training complete — ${proposed} new rule(s) proposed for approval. ` +
          `Avg simulated accuracy: ${acc != null ? Math.round(acc * 100) + '%' : 'N/A'}.`
        );
      } else if (finalRun && finalRun.status === 'failed') {
        setError('Self-training run failed during execution. Please try again.');
      } else {
        setNotice('Training is still running in the background — refresh in a moment to see new rules.');
      }
      setActiveTab('pending');
      await loadData(selectedTenant);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || 'Self-training run failed to start');
    } finally {
      setTraining(false);
    }
  };

  const proposeFromReviews = async () => {
    if (!selectedTenant) return;
    setProposing(true);
    setError(null);
    setNotice(null);
    try {
      const res = await axios.post(`${API}/training/propose-from-reviews/${selectedTenant}`);
      const count = res.data?.count ?? 0;
      setNotice(
        count > 0
          ? `${count} rule(s) proposed from staff reviews — pending approval.`
          : 'No new rules could be derived from staff reviews yet.'
      );
      setActiveTab('pending');
      await loadData(selectedTenant);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || 'Failed to propose from reviews');
    } finally {
      setProposing(false);
    }
  };

  const learnFromSessions = async () => {
    if (!selectedTenant) return;
    setLearningSessions(true);
    setError(null);
    setNotice(null);
    try {
      const res = await axios.post(`${API}/training/learn-from-sessions/${selectedTenant}`);
      const count = res.data?.count ?? 0;
      setNotice(
        count > 0
          ? `${count} rule(s) proposed from real intake sessions — pending approval.`
          : 'No completed intake sessions to learn from yet.'
      );
      setActiveTab('pending');
      await loadData(selectedTenant);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || 'Failed to learn from sessions');
    } finally {
      setLearningSessions(false);
    }
  };

  const reviewRule = async (ruleId, action) => {
    try {
      await axios.post(`${API}/training/rules/${ruleId}/${action}`, {
        reviewed_by: user?.email || 'admin',
      });
      await loadData(selectedTenant);
    } catch (e) {
      console.error(e);
      setError(`Failed to ${action} rule`);
    }
  };

  const counts = overview?.rule_counts || { pending: 0, approved: 0, rejected: 0 };
  const filteredRules = rules.filter((r) => r.status === activeTab);
  const selectedTenantObj = tenants.find((t) => t.tenant_id === selectedTenant);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20">
            <GraduationCap className="w-5 h-5 text-emerald-400" />
            <span className="text-white font-medium">Training Center</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            AI Self-Training &amp;{' '}
            <span className="bg-gradient-to-r from-emerald-400 to-cyan-500 bg-clip-text text-transparent">
              Learning
            </span>
          </h1>
          <p className="text-sm sm:text-base text-slate-300">
            Run self-play simulations, review AI-discovered improvements, and approve what goes live.
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-col lg:flex-row gap-3 lg:items-end lg:justify-between bg-slate-800/50 border border-slate-700 rounded-xl p-4">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <div className="space-y-1 flex-1 max-w-sm">
              <label className="text-xs text-slate-400">Tenant</label>
              <Select value={selectedTenant} onValueChange={setSelectedTenant}>
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white" data-testid="training-tenant-select">
                  <SelectValue placeholder="Choose tenant..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 text-white">
                  {tenants.map((t) => (
                    <SelectItem key={t.tenant_id} value={t.tenant_id}>
                      {t.name} · {t.industry_type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 w-full sm:w-40">
              <label className="text-xs text-slate-400">Simulations</label>
              <Select value={numSims} onValueChange={setNumSims}>
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 text-white">
                  {['1', '2', '3', '5'].map((n) => (
                    <SelectItem key={n} value={n}>{n} run{n === '1' ? '' : 's'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
            <Button
              onClick={learnFromSessions}
              disabled={learningSessions || training || proposing || !selectedTenant}
              variant="outline"
              className="border-teal-600/50 text-teal-200 hover:bg-teal-700/30 w-full sm:w-auto"
              data-testid="learn-sessions-button"
            >
              {learningSessions ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              From Real Sessions
            </Button>
            <Button
              onClick={proposeFromReviews}
              disabled={proposing || training || learningSessions || !selectedTenant}
              variant="outline"
              className="border-slate-600 text-slate-200 hover:bg-slate-700 w-full sm:w-auto"
              data-testid="propose-reviews-button"
            >
              {proposing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              From Staff Reviews
            </Button>
            <Button
              onClick={runTraining}
              disabled={training || proposing || learningSessions || !selectedTenant}
              className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white w-full sm:w-auto"
              data-testid="run-training-button"
            >
              {training ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              {training ? 'Training…' : 'Run Self-Training'}
            </Button>
          </div>
        </div>
      </div>

      {notice && (
        <Alert className="bg-emerald-500/10 border-emerald-500/30 text-emerald-300">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert className="bg-red-500/10 border-red-500/20 text-red-400">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {training && (
        <div className="text-center py-4 text-slate-300 text-sm">
          <Activity className="w-5 h-5 inline mr-2 text-emerald-400 animate-pulse" />
          Running simulated conversations and self-critique… this can take ~20–40s.
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard icon={Clock} label="Pending Review" value={counts.pending} color="text-amber-400" testid="stat-pending" />
        <StatCard icon={CheckCircle2} label="Approved (Live)" value={counts.approved} color="text-emerald-400" testid="stat-approved" />
        <StatCard icon={XCircle} label="Rejected" value={counts.rejected} color="text-slate-400" testid="stat-rejected" />
        <StatCard
          icon={TrendingUp}
          label="Latest Accuracy"
          value={overview?.latest_accuracy != null ? `${Math.round(overview.latest_accuracy * 100)}%` : '—'}
          color="text-cyan-400"
          testid="stat-accuracy"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Rules management */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-emerald-400" />
                Learned Rules
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-3 bg-slate-900/60">
                  <TabsTrigger value="pending" data-testid="tab-pending">Pending ({counts.pending})</TabsTrigger>
                  <TabsTrigger value="approved" data-testid="tab-approved">Approved ({counts.approved})</TabsTrigger>
                  <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected ({counts.rejected})</TabsTrigger>
                </TabsList>
                <TabsContent value={activeTab} className="mt-4 space-y-3">
                  {loading ? (
                    [1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full bg-slate-700" />)
                  ) : filteredRules.length === 0 ? (
                    <div className="text-center py-10 text-slate-400">
                      <Brain className="w-10 h-10 mx-auto mb-3 opacity-40" />
                      <p>No {activeTab} rules yet.</p>
                      {activeTab === 'pending' && (
                        <p className="text-xs mt-1">Run self-training to generate improvement suggestions.</p>
                      )}
                    </div>
                  ) : (
                    filteredRules.map((rule) => (
                      <div
                        key={rule.rule_id}
                        className="p-4 rounded-lg bg-slate-900/50 border border-slate-700"
                        data-testid="rule-card"
                      >
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <Badge variant="outline" className={categoryColors[rule.category] || categoryColors.general}>
                            {rule.category}
                          </Badge>
                          <Badge variant="outline" className="border-slate-600 text-slate-400 text-xs">
                            {rule.source === 'self_training' ? 'Self-Training'
                              : rule.source === 'real_sessions' ? 'Real Sessions'
                              : 'Staff Review'}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            confidence {Math.round((rule.confidence || 0) * 100)}%
                          </span>
                        </div>
                        <p className="text-sm text-slate-200">{rule.rule_text}</p>
                        {rule.evidence && (
                          <p className="text-xs text-slate-500 mt-2 italic">Evidence: {rule.evidence}</p>
                        )}
                        {rule.status === 'pending' && (
                          <div className="flex gap-2 mt-3">
                            <Button
                              size="sm"
                              onClick={() => reviewRule(rule.rule_id, 'approve')}
                              className="bg-emerald-500/90 hover:bg-emerald-600 text-white"
                              data-testid="approve-rule-button"
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => reviewRule(rule.rule_id, 'reject')}
                              className="border-slate-600 text-slate-300 hover:bg-slate-700"
                              data-testid="reject-rule-button"
                            >
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </div>
                        )}
                        {rule.status !== 'pending' && rule.reviewed_by && (
                          <p className="text-xs text-slate-500 mt-2">
                            {rule.status} by {rule.reviewed_by}
                          </p>
                        )}
                      </div>
                    ))
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Right column: trend + runs */}
        <div className="space-y-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-base">Accuracy Trend</CardTitle>
            </CardHeader>
            <CardContent>
              {overview?.accuracy_trend?.length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={overview.accuracy_trend.map((d) => ({ ...d, accuracyPct: Math.round((d.accuracy || 0) * 100) }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="run" stroke="#64748b" fontSize={11} />
                    <YAxis domain={[0, 100]} stroke="#64748b" fontSize={11} />
                    <Tooltip
                      contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#fff' }}
                      formatter={(v) => [`${v}%`, 'Accuracy']}
                    />
                    <Line type="monotone" dataKey="accuracyPct" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-slate-400 text-sm">
                  <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  No training runs yet
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-cyan-400" /> Recent Runs
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {runs.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No runs yet</p>
              ) : (
                runs.slice(0, 6).map((r) => (
                  <div key={r.run_id} className="flex items-center justify-between p-2 rounded bg-slate-900/40 text-sm">
                    <div>
                      <p className="text-slate-200">{r.completed_simulations}/{r.num_simulations} sims</p>
                      <p className="text-xs text-slate-500">
                        {r.started_at ? new Date(r.started_at).toLocaleString() : ''}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-emerald-400 font-medium">{Math.round((r.avg_accuracy || 0) * 100)}%</p>
                      <p className="text-xs text-slate-500">{r.rules_proposed} rules</p>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {selectedTenantObj && (
        <p className="text-center text-xs text-slate-500">
          Approved rules are automatically injected into live AI sessions for{' '}
          <span className="text-slate-300">{selectedTenantObj.name}</span>.
        </p>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color, testid }) => (
  <Card className="bg-slate-800 border-slate-700">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">{label}</span>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <div className={`text-2xl font-bold mt-1 ${color}`} data-testid={testid}>{value}</div>
    </CardContent>
  </Card>
);

export default TrainingCenter;
